var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from "mobx";
import { isErr } from "healthex-common/result";
import { NamedRoutes } from "./NamedRoutes";
import { AuthType } from "healthex-common/auth.dtos";
import { LoginPage } from "./LoginUIManager";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
const t = getObservableT("common");
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export class LoginViewModel {
    constructor(container) {
        this.input = { email: "", password: "" };
        this.authTypeResponse = null;
        this.error = null;
        this.organizations = null;
        this.selectedOrganizationId = null;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.loginUIManager = container.loginUIManager;
        const org = this.loginUIManager.organization;
        if (org && org !== NOT_LOADED && !org.ssoEnabled) {
            this.authTypeResponse = { authType: AuthType.USERNAME_PASSWORD };
        }
    }
    get organization() {
        return this.loginUIManager.organization;
    }
    get isLoggedIn() {
        return this.authManager.isLoggedIn;
    }
    updateInput(update) {
        this.input = {
            ...this.input,
            ...Object.entries(update).reduce((acc, [key, value]) => {
                acc[key] = key === "email" ? value.trim() : value;
                return acc;
            }, {}),
        };
    }
    resetAuthResponse() {
        this.authTypeResponse = null;
        this.selectedOrganizationId = null;
        this.organizations = null;
    }
    resetToEmailEntry() {
        this.organizations = null;
        this.authTypeResponse = null;
        this.selectedOrganizationId = null;
        this.input = { email: "", password: "" };
    }
    get isAuthTypePassword() {
        return this.authTypeResponse && this.authTypeResponse.authType === AuthType.USERNAME_PASSWORD;
    }
    get isFormValid() {
        return this.isEmailValid && this.input.password !== "";
    }
    get isEmailValid() {
        return EMAIL_REGEX.test(this.input.email);
    }
    get brandedOrgId() {
        const org = this.loginUIManager.organization;
        return org !== NOT_LOADED ? org === null || org === void 0 ? void 0 : org.id : undefined;
    }
    applyAuthTypeResponse(response) {
        this.authTypeResponse = response;
        if (response.authType === AuthType.SSO_SAML && response.redirectUrl) {
            window.location.href = response.redirectUrl;
        }
    }
    async resolveGenericAuthType() {
        const orgs = await this.authManager.getOrganizationsForEmail(this.input.email, window.location.href);
        if (orgs.length === 0) {
            const response = await this.authManager.getAuthTypeByEmail(this.input.email, window.location.href);
            this.applyAuthTypeResponse(response);
        }
        else {
            this.organizations = orgs;
        }
    }
    async fetchAuthType() {
        this.error = null;
        if (this.brandedOrgId) {
            const response = await this.authManager.getAuthTypeByOrgId(this.brandedOrgId, window.location.href);
            this.applyAuthTypeResponse(response);
        }
        else {
            await this.resolveGenericAuthType();
        }
    }
    selectOrganization(orgId) {
        var _a;
        const org = (_a = this.organizations) === null || _a === void 0 ? void 0 : _a.find((o) => o.id === orgId);
        this.selectedOrganizationId = orgId;
        this.organizations = null;
        if ((org === null || org === void 0 ? void 0 : org.authType) === AuthType.SSO_SAML && org.ssoRedirectUrl) {
            window.location.href = org.ssoRedirectUrl;
        }
        else {
            this.authTypeResponse = { authType: AuthType.USERNAME_PASSWORD };
        }
    }
    forgotPassword() {
        var _a;
        const orgId = (_a = this.selectedOrganizationId) !== null && _a !== void 0 ? _a : this.brandedOrgId;
        this.routingUIManager.navigateTo(NamedRoutes.FORGOT_PASSWORD, { organizationId: orgId });
    }
    async login() {
        var _a;
        const orgId = (_a = this.selectedOrganizationId) !== null && _a !== void 0 ? _a : this.brandedOrgId;
        const result = await this.authManager.authenticateByPassword(this.input.email, this.input.password, orgId);
        if (isErr(result)) {
            switch (result.error) {
                case "INCORRECT_CREDENTIALS":
                    this.error = t("Incorrect email or password! Please try again.");
                    break;
                case "ACCOUNT_LOCKED":
                    this.error = t("Your account is temporarily locked to prevent unauthorized use. You can either reset your password or try again later. If you still have trouble, contact your administrator.");
                    break;
                case "EXPIRED_PASSWORD":
                    this.error = t("Your password has expired and must be changed to proceed.");
                    this.routingUIManager.navigateTo(NamedRoutes.EXPIRED_PASSWORD, {
                        userEmailAddress: this.input.email,
                        organizationId: orgId,
                    });
                    break;
                case "ADMIN_PASSWORD_RESET":
                    this.error = t("Your adminstrator has forced you to change your password. Instructions have been sent to your email. If you still have trouble, contact your administrator.");
                    break;
                default:
                    this.error = t("An unexpected error occurred");
            }
        }
        else {
            this.error = null;
        }
    }
    loginAsPatient() {
        this.loginUIManager.setLoginPage(LoginPage.PATIENT_LOGIN, undefined);
    }
}
__decorate([
    observable
], LoginViewModel.prototype, "input", void 0);
__decorate([
    observable
], LoginViewModel.prototype, "authTypeResponse", void 0);
__decorate([
    observable
], LoginViewModel.prototype, "error", void 0);
__decorate([
    observable
], LoginViewModel.prototype, "organizations", void 0);
__decorate([
    observable
], LoginViewModel.prototype, "selectedOrganizationId", void 0);
__decorate([
    computed
], LoginViewModel.prototype, "organization", null);
__decorate([
    computed
], LoginViewModel.prototype, "isLoggedIn", null);
__decorate([
    computed
], LoginViewModel.prototype, "isAuthTypePassword", null);
__decorate([
    computed
], LoginViewModel.prototype, "isFormValid", null);
__decorate([
    computed
], LoginViewModel.prototype, "isEmailValid", null);
__decorate([
    computed
], LoginViewModel.prototype, "brandedOrgId", null);
